import { Button, Typography } from '@mui/material';
import { arrayOf, object, string } from 'prop-types';
import React, { useCallback, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import Login from 'services/api/Login';
import { connect, disconnect, isConnected } from 'services/LoginSocket';
import { I18n } from 'utils/i18n';
import connector from '../connector';
import Heading from '../Heading';
import ConfirmationForm from './ConfirmationForm';

const CONFIRM_LOGIN_POLL_MS = 4000;

const ConfirmationScene = ({
  loginType = null,
  confirmToken = null,
  deviceNames = [],
  actions,
  form,
  lastCompany = null,
  returnUrl = null,
}) => {
  const navigate = useNavigate();
  const locale = useSelector(state => state.i18n.locale);
  const loginSettledRef = useRef(false);
  const { loginSuccess } = actions.auth;

  const onLoginRejected = useCallback(() => {
    navigate('/auth/login/rejected');
  }, [navigate]);

  const completeLogin = useCallback(
    ({ token, user, keepSessionOpen, loginMethod }) => {
      if (loginSettledRef.current) {
        return;
      }
      loginSettledRef.current = true;
      loginSuccess({ token, user, keepSessionOpen, loginMethod, returnUrl });
    },
    [loginSuccess, returnUrl],
  );

  const rejectLogin = useCallback(() => {
    if (loginSettledRef.current) {
      return;
    }
    loginSettledRef.current = true;
    onLoginRejected();
  }, [onLoginRejected]);

  const isAppConfirmation = loginType === 'sms' && confirmToken;

  useEffect(() => {
    if (!isAppConfirmation) {
      return undefined;
    }
    if (!isConnected()) {
      connect(confirmToken, completeLogin, rejectLogin);
    }
    return () => disconnect();
  }, [completeLogin, confirmToken, isAppConfirmation, rejectLogin]);

  useEffect(() => {
    if (!isAppConfirmation) {
      return undefined;
    }

    let cancelled = false;

    const pollConfirmLogin = async () => {
      try {
        const { data } = await Login.pollConfirmLogin(confirmToken);
        if (cancelled || data?.state !== 'confirmed') {
          return;
        }
        completeLogin({
          token: data.token,
          user: data.user,
          keepSessionOpen: data.keepSessionOpen,
          loginMethod: data.loginMethod,
        });
      } catch (error) {
        if (cancelled || loginSettledRef.current) {
          return;
        }
        if (error?.status === 404) {
          rejectLogin();
        }
      }
    };

    pollConfirmLogin();
    const intervalId = setInterval(pollConfirmLogin, CONFIRM_LOGIN_POLL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [completeLogin, confirmToken, isAppConfirmation, rejectLogin]);

  const count = deviceNames.length;
  let devices = '';
  for (let idx = 0; idx < deviceNames.length; idx += 1) {
    devices += `<b>${deviceNames[idx]}</b>`;
    if (idx === deviceNames.length - 2) {
      devices += I18n.t('LoginConfirmation.and');
    } else if (idx < deviceNames.length - 2) {
      devices += ', ';
    }
  }
  const type = loginType === 'sms' && confirmToken ? 'app' : loginType;

  const onSendSMS = async () => {
    await actions.login.sendSMS(`${form.prefix}${form.phone}`, { forceSms: true, locale });
    navigate('/auth/login/confirmation', { replace: true });
  };

  return (
    <div>
      <Heading
        type={type}
        headline={I18n.t(`LoginConfirmation.headline ${type}`)}
        subheading={I18n.t(`LoginConfirmation.subheading ${type}`, {
          devices,
          count,
          dangerousHTML: true,
        })}
      />
      {type === 'app' ? (
        lastCompany?.allowOnlyAuthorizedDevices || (
          <>
            <Typography gutterBottom>{I18n.t('LoginConfirmation.If you did not')}</Typography>
            <Button color="primary" onClick={onSendSMS}>
              {I18n.t('LoginConfirmation.Send SMS')}
            </Button>
          </>
        )
      ) : (
        <ConfirmationForm />
      )}
    </div>
  );
};
ConfirmationScene.propTypes = {
  loginType: string,
  confirmToken: string,
  deviceNames: arrayOf(string),
  actions: object.isRequired,
  form: object.isRequired,
  lastCompany: object,
  returnUrl: string,
};

export default connector(ConfirmationScene);
